import { useState } from 'react'
import Styles from '../ShapesTab.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { ChevronDown2 } from 'blaze-ui/icons'
import { useUpdateStyle } from 'components/VisualStudioPlayground/VisualEditor/hooks/useUpdateStyle'
import ToggleButton from 'common/ToggleButton/ToggleButton'
import { Editor, SharedStyle, track } from '@tldraw/tldraw'
import {
  ITextBoxBackgroundFillStyle,
  TextBoxBackgroundFillStyle,
  TextBoxBackgroundShapeStyle,
  TextColorStyle,
} from 'components/VisualStudioPlayground/VisualEditor/StyleProps'
import { Background, ChevronRightSmall } from 'blaze-ui/icons/24'
import { SolidColor } from 'components/VisualStudioPlayground/VisualEditor/Types'
import { ColorDetailRow } from './ColorDetailRow'
import { Button } from 'blaze-ui/Button'

interface Props {
  editor: Editor
  fillStyle: SharedStyle<string | ITextBoxBackgroundFillStyle>
  shapeStyle: SharedStyle<string>
}

const defaultSolidColor: SolidColor = { type: 'solidColor', color: '#000000', opacity: 1 }

export const TextBoxBackground = track(({ editor, fillStyle, shapeStyle }: Props) => {
  const updateStyle = useUpdateStyle(editor)
  const [isExpanded, setIsExpanded] = useState(false)

  if (fillStyle.type === 'mixed' || shapeStyle.type === 'mixed') return null

  const isActive = Boolean(fillStyle.value)

  const formStyle = fillStyle.value ?? defaultSolidColor

  return (
    <div className={Styles.colorSection}>
      <div className={Styles.actionRow} onClick={() => setIsExpanded(!isExpanded)}>
        <Background size={20} color="var(--selected-fill)" />
        <span className={Styles.actionName}>
          <Text variant="smallList">Background</Text>
        </span>
        <ToggleButton
          name="background"
          size="sm-md"
          checked={isActive}
          handleChange={() => {
            if (isActive) {
              updateStyle(TextBoxBackgroundFillStyle, null)
              updateStyle(TextColorStyle, '#000000')
              setIsExpanded(false)
            } else {
              updateStyle(TextBoxBackgroundFillStyle, defaultSolidColor)
              updateStyle(TextColorStyle, '#FFFFFF')
              setIsExpanded(true)
            }
          }}
        />
        {isExpanded ? <ChevronDown2 color="var(--dark-60)" /> : <ChevronRightSmall size={20} color="var(--dark-60)" />}
      </div>
      {isExpanded && (
        <div className={Styles.expandedSection}>
          <div className={Styles.form}>
            <div className={Styles.field}>
              <ColorDetailRow
                editor={editor}
                label="Background Color"
                color={(formStyle as SolidColor).color}
                updateColor={(color) =>
                  updateStyle(TextBoxBackgroundFillStyle, { type: 'solidColor', color, opacity: 1 })
                }
              />
            </div>

            <div className={Styles.field}>
              <Button
                variant="tertiary"
                className={shapeStyle.value === 'silhouetteSquare' ? Styles.buttonOn : ''}
                aria-pressed={shapeStyle.value === 'silhouetteSquare'}
                onPress={() => updateStyle(TextBoxBackgroundShapeStyle, 'silhouetteSquare')}
              >
                Silhouette Square
              </Button>
              <Button
                variant="tertiary"
                className={shapeStyle.value === 'silhouetteRounded' ? Styles.buttonOn : ''}
                aria-pressed={shapeStyle.value === 'silhouetteRounded'}
                onPress={() => updateStyle(TextBoxBackgroundShapeStyle, 'silhouetteRounded')}
              >
                Silhouette Rounded
              </Button>
              <Button
                variant="tertiary"
                className={shapeStyle.value === 'box' ? Styles.buttonOn : ''}
                aria-pressed={shapeStyle.value === 'box'}
                onPress={() => updateStyle(TextBoxBackgroundShapeStyle, 'box')}
              >
                Boxed
              </Button>
              <Button
                variant="tertiary"
                className={shapeStyle.value === 'callToAction' ? Styles.buttonOn : ''}
                aria-pressed={shapeStyle.value === 'callToAction'}
                onPress={() => updateStyle(TextBoxBackgroundShapeStyle, 'callToAction')}
              >
                Call to Action
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
})
